import always from 'ramda/src/always'
import tryCatch from 'ramda/src/tryCatch'
import concat from 'ramda/src/concat'
import pipe from 'ramda/src/pipe'
import find from 'ramda/src/find'
import head from 'ramda/src/head'
import pluck from 'ramda/src/pluck'
import join from 'ramda/src/join'
import map from 'ramda/src/map'
import path from 'ramda/src/path'
import propEq from 'ramda/src/propEq'
import anyPass from 'ramda/src/anyPass'
import uniq from 'ramda/src/uniq'
import { DROP_ADMIN_LIST_MODULE_ID, ADMIN_CAMPAIGN_MANAGEMENT_MODAL_MODULE_ID } from 'root/src/shared/descriptions/modules/moduleIds'
import { tableListType } from 'root/src/shared/constants/moduleTypes'
import { dropEvent } from 'root/src/shared/descriptions/endpoints/recordTypes'
import {
	GET_DROP_EVENTS, ADMIN_GET_TOKEN, DROP_ACTIVATE_EVENT, DROP_ADMIN_AUDIT,
	CHANGE_CAMPAIGN_STATUS, DROP_GET_EVENT,
} from 'root/src/shared/descriptions/endpoints/endpointIds'
import { adminKey } from 'root/src/shared/constants/keys'
import outputs from 'root/cfOutput'
import moment from 'moment'
import { HOMEPAGE_ROUTE_ID } from 'root/src/shared/descriptions/routes/routeIds'
import invokeApiRequest from 'root/src/client/logic/api/util/invokeApiRequest'
import linkHrefSelector from 'root/src/client/logic/app/selectors/linkHrefSelector'
import changeRouteCommon from 'root/src/client/logic/brandSettings/util/changeRouteCommon'
import displayModal from 'root/src/client/logic/modal/thunks/displayModal'
import { auditEnum, statusEnum } from 'root/src/shared/constants/enums'
import wait from 'root/src/shared/util/wait'
import { dropEventActivateMethodsEnum, dropEventStatusEnum, dropEventBudgetTypes } from 'root/src/shared/constants/dropEvent'
import editDropEvent from 'root/src/client/logic/dropEvent/thunks/editDropEvent'

const adminRelog = item => async (dispatch, getState) => {
	const brandId = path(['brand', 'id'], item)
	const { body: userToken } = await invokeApiRequest(
		ADMIN_GET_TOKEN,
		{ userId: brandId },
		getState(),
	)

	const href = linkHrefSelector(
		{},
		{ routeId: HOMEPAGE_ROUTE_ID, hash: userToken },
	)
	changeRouteCommon(getState, href, HOMEPAGE_ROUTE_ID)
}

const showManagementModal = item => async (dispatch, getState) => {
	// Get drop event data from database to avoid reading not updated data from ES
	const { body } = await invokeApiRequest(
		DROP_GET_EVENT,
		{ id: item.id },
		getState(),
	)
	dispatch(
		displayModal(ADMIN_CAMPAIGN_MANAGEMENT_MODAL_MODULE_ID, {
			dispatch,
			getState,
			item: body,
		}),
	)
}

const approve = (item, { apiRequest }) => async (dispatch, getState) => {
	const { id } = item
	const brandId = path(['brand', 'id'], item)
	const budgetType = path(['budget', 'type'], item)
	const handleError = (res) => {
		if (res.statusCode !== 200) {
			throw res.error
		}
	}

	handleError(await apiRequest(DROP_ADMIN_AUDIT, {
		audit: auditEnum.ACTIVATE, id,
	})(dispatch, getState))

	await wait(2000)

	if (budgetType === dropEventBudgetTypes.CAMPAIGN) {
		handleError(await apiRequest(DROP_ACTIVATE_EVENT, {
			id,
			method: dropEventActivateMethodsEnum.INVOICE,
		})(dispatch, getState))
	} else {
		handleError(await apiRequest(CHANGE_CAMPAIGN_STATUS, {
			action: statusEnum.ENABLE,
			dropEventId: id,
			brandId,
		})(dispatch, getState))
	}
	await wait(2000)

	await apiRequest(GET_DROP_EVENTS, {
		section: adminKey,
		pageItemLength: 20,
	}, true, DROP_ADMIN_LIST_MODULE_ID)(dispatch, getState)
}

export default {
	[DROP_ADMIN_LIST_MODULE_ID]: {
		moduleType: 'list',
		listType: tableListType,
		tableColumns: [
			['Email', 'email'],
			['Publisher name', ['brand', 'brandName']],
			['Game', 'dares', pipe(pluck('games'), map(head), pluck('label'), uniq, join(', '))],
			['Similar games', 'similarGames', pipe(pluck('label'), uniq, join(', '))],
			['Awards points', 'awardPoints', awardPoints => (awardPoints ? 'yes' : 'no')],
			['Campaign url', 'mnemonicId', concat(`${outputs.domainName}/e/`)],
			[
				'Launch date',
				'liveDate',
				liveDate => liveDate && moment.utc(liveDate, 'YYYY-MM-DD').format('MMM DD YYYY'),
			],
			[
				'Tier',
				['subscriptionData', 'planId'],
				(planId) => {
					if (!planId) return undefined
					const plan = find(
						([, value]) => value === planId,
						// eslint-disable-next-line no-undef
						Object.entries(PAYPAL_SUBSCRIPTIONS),
					)
					return tryCatch(head, always('custom'))(plan)
				},
			],
			['Plan id', ['subscriptionData', 'planId']],
			['Drop id', 'id'],
			['Initial product', 'initialProduct', initialProduct => (initialProduct ? 'yes' : 'no')],
			['Waiting for live', 'waitingForLive'],
			['Waiting for product', 'waitingForProduct'],
			[
				'CCV Breakpoints',
				'ccvBreakpoints',
				(ccvBreakpoints) => {
					if (!ccvBreakpoints) {
						return []
					}
					return map(
						breakpoint => `${breakpoint.ccv} : ${breakpoint.maxDares}`,
						ccvBreakpoints,
					)
				},
			],
			[
				'Follower Breakpoints',
				'followersBreakpoints',
				(followersBreakpoints) => {
					if (!followersBreakpoints) {
						return []
					}
					return map(
						breakpoint => `${breakpoint.followerCount} : ${breakpoint.maxDares}`,
						followersBreakpoints,
					)
				},
			],
		],
		tableItemActions: [
			{
				name: 'Approve',
				fn: approve,
				order: 1,
				hide: tryCatch(anyPass([
					propEq('status', dropEventStatusEnum.ACTIVE),
					propEq('status', dropEventStatusEnum.PENDING_GO_LIVE_DATE),
					propEq('status', dropEventStatusEnum.EXPIRED),
				]), always(false)),
			},
			{
				name: 'Edit',
				fn: editDropEvent,
				order: 2,
			},
			{
				name: 'Manage',
				fn: showManagementModal,
				order: 3,
			},
			{
				name: 'Log-in as publisher',
				fn: adminRelog,
				order: 4,
			},
		],
		listTitle: 'Drop campaigns',
		recordType: dropEvent,
		endpointId: [GET_DROP_EVENTS],
		listPayload: {
			section: adminKey,
			pageItemLength: 20,
		},
	},
}
